import { OwnedPlanet, PlanetDeed, TileUpgrade } from '@prometheus/types'
import { round } from 'lodash'
import { isPlanetHoldingForUpgrade, truncateCash, yearsUntilNextYield } from '@prometheus/utils'
import formatCost from '../../../../utils/formatCost'

// Pure view-model builders for the phone "extreme compact" planet card:
// name + Pop/Req for the current level and the next four, tile progress, and
// mine chips. Kept free of React/redux so they are directly unit-testable.

export interface CompactLevel {
  lvl: number
  pop: string
  req: string
  isCurrent: boolean
}

// The current level plus the next `count - 1`. Near the end of the deed the
// window slides back so the card always shows `count` levels when they exist.
// Pass currentLevel = -1 for a planet the player does not own: the window
// starts at level 1 and no column is highlighted.
export function buildCompactLevels(deed: PlanetDeed, currentLevel: number, count = 5): CompactLevel[] {
  const levels = deed.levels || []
  const start = Math.max(0, Math.min(currentLevel, levels.length - count))
  return levels.slice(start, start + count).map((level, i) => ({
    lvl: start + i + 1,
    pop: truncateCash(level.pop, { raw: true }),
    req: (level.cost && formatCost(level.cost, { ignoreCash: true, removeDigits: true })) || '—',
    isCurrent: start + i === currentLevel,
  }))
}

export interface CompactMineChip {
  key: string
  resource: string
  label: string
  active: boolean
  // Slot data so the compact card can drive the same purchase flow as the
  // full card (toggle a single mine, or open the quantity modal).
  index: number
  current: number
  max: number
}

// Mirrors the full card's mine labels: "2/4 Water" when the planet supports
// multiple mines of a resource, otherwise just the resource name.
export function buildCompactMineChips(ownedPlanet: OwnedPlanet, deed: PlanetDeed): CompactMineChip[] {
  const chips: CompactMineChip[] = []
  ownedPlanet.mines.forEach((mine, i) => {
    const m = Object.keys(mine)[0]
    if (!m) return
    // Atomic symbol, not the full resource name — chips stay chip-sized.
    let label = m
    if (deed.mine_production_maximum[m] > 1) {
      label = `${truncateCash(mine[m], { raw: true, removeDigits: true })}/${truncateCash(deed.mine_production_maximum[m], { raw: true, removeDigits: true })} ${m}`
    }
    chips.push({
      key: `${m}${i}`,
      resource: m,
      label,
      active: !!mine[m],
      index: i,
      current: mine[m],
      max: deed.mine_production_maximum[m],
    })
  })
  return chips
}

// Mine chips for a planet the player does NOT own (travel/purchase lists):
// the deed's available mine types, with capacity when more than one fits.
// Mirrors the full card's !owned branch. All chips render inactive.
export function buildUnownedMineChips(deed: PlanetDeed): CompactMineChip[] {
  return (deed.mines || []).map((mine, i) => {
    // Atomic symbol, matching the owned chips.
    let label = mine
    if (deed.mine_production_maximum[mine] > 1) {
      label = `${truncateCash(deed.mine_production_maximum[mine], { raw: true, removeDigits: true })} ${mine}`
    }
    return {
      key: `${mine}${i}`,
      resource: mine,
      label,
      active: false,
      index: i,
      current: 0,
      max: deed.mine_production_maximum[mine],
    }
  })
}

// Tile progress, matching the full card's button: sub-level progress when
// planet tokens are disabled, otherwise the dice tile number.
//
// A planet that finished its cycle but could not pay for the next level holds
// its progress and retries every year, which reads as a nonsensical "3/2.1"
// with nothing to explain it. The hourglass marks that state.
export function buildTileLabel(ownedPlanet: OwnedPlanet, disablePlanetTokens: boolean): string {
  if (disablePlanetTokens) {
    if (isPlanetHoldingForUpgrade(ownedPlanet)) {
      // Progress is frozen and the fraction would read as a nonsensical
      // "3/2.1", so show why it is stopped and when the mines next pay instead.
      const yieldIn = yearsUntilNextYield(ownedPlanet, disablePlanetTokens)
      return `🔒 ⛏${yieldIn ?? '?'}y`
    }
    return `${round(ownedPlanet.sub_levels_current || 0, 1)}/${ownedPlanet.tile}`
  }
  return `${ownedPlanet.tile}`
}

// Head-chip label for a planet the player does not own: the rent owed when
// traveling to another player's planet, otherwise the purchase price.
export function buildPriceLabel(deed: PlanetDeed): string {
  if (typeof deed.rent === 'number') {
    return `Rent ${truncateCash(deed.rent)}`
  }
  return truncateCash(deed.levels?.[0]?.cost?.cash || 0)
}

// Whether a planet renders as a compact card in compact mode: deed planets
// only (starbases/stargates have no level table to compact), and owned-list
// rows need their OwnedPlanet payload. Shared by the renderer and by the
// "any adjacent cards?" count — a compact card is only worth it when at
// least two would sit side by side; a lone card falls back to the full card.
export function isCompactEligible(
  deedType: string | undefined,
  owned: boolean,
  hasOwnedPlanet: boolean,
): boolean {
  return deedType === 'deed' && (!owned || hasOwnedPlanet)
}

// Whether the travel action on a choice card is locked, mirroring the full
// card's rule: on decennial turns past turn 19 players pick planets one at a
// time — only the first player in the choosing queue may move, and never to
// a planet another ship already occupies.
export function isTravelChoiceLocked(
  turn: number,
  uid: string,
  playersChoosingPlanet: string[],
  occupiedPlanets: string[],
  planet: string,
): boolean {
  if (turn % 10 !== 0 || turn <= 19) {
    return false
  }
  return playersChoosingPlanet[0] !== uid || occupiedPlanets.includes(planet)
}

export interface NextTileUpgrade {
  /** Resource symbol, or the cash amount as a string for a cash rung. */
  label: string
  /** How many units the rung costs (undefined for a cash rung). */
  quantity?: number
  /** Whether the ladder has a rung left at all. */
  available: boolean
}

/**
 * What the planet's next tile upgrade costs.
 *
 * Lifted out of the full card so the phone card can show the same thing, and so
 * the derivation — which has to cope with dice tiles appearing in either slot
 * of `from_numbers` — is testable on its own.
 */
export function getNextTileUpgrade(
  tile: number,
  tileUpgrades: TileUpgrade[],
  disablePlanetTokens: boolean,
): NextTileUpgrade {
  if (!tileUpgrades?.length) {
    return { label: '', available: false }
  }

  let index = tileUpgrades.findIndex(x => x.from_numbers[0] === tile)
  if (index === -1 && !disablePlanetTokens) {
    index = tileUpgrades.findIndex(x => x.from_numbers[1] === tile)
  }
  if (!index && disablePlanetTokens) {
    index = 0
  }
  if (index === -1 || index + 1 >= tileUpgrades.length) {
    return { label: '', available: false }
  }

  const cost = tileUpgrades[index].cost
  for (const value of Object.values(cost)) {
    // A resource rung looks like { resources: { Cu: 1 } }; a cash rung is a
    // bare number, which has no keys to read.
    const resource = value && typeof value === 'object' ? Object.keys(value)[0] : undefined
    if (resource) {
      return { label: resource, quantity: (value as Record<string, number>)[resource], available: true }
    }
  }
  return { label: cost.cash?.toString() || '', available: true }
}
